// A staff link opens the client that owns it (#10718 slice 4).
//
// Renders nothing. Mounted with the app shell (`Layout`), in a staff member's
// home tab only. When a read of the page is refused (`fpFetch` announces a
// GET answered 403 or 404) and the page is one that shows a single resource
// (a chat, a document, a record), it asks the server once for this path. The
// server opens the client that owns the resource, taking admin access only
// when that is what it takes to see it, and answers with a one-time code; the
// tab goes to the entry page with this path as `next`, which shows nothing
// while it exchanges the code, so the page simply appears to load. A 404 or
// 409 answer leaves the page's own error in place, and the path is not asked
// about again in this tab.
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import fpFetch from "@/services/fpFetch";
import {
  clientEnvironmentEnterUrl,
  holdsTokenFor,
  isClientEnvironment,
  markTabForClient,
} from "@/services/clientEnvironment";
import {
  FOR_PATH_URL,
  knownToOpenNothing,
  namesResource,
  rememberOpensNothing,
  RESOURCE_REFUSED_EVENT,
} from "@/services/staffLinkEntry";

interface StaffLinkEntryProps {
  /** Loads `url` in this tab; replaced in stories. A full load, so the tab's
   * mode is decided again. */
  onEnter?: (url: string) => void;
}

const load = (url: string): void => {
  window.location.replace(url);
};

interface OpenedForPath {
  grant: { id: number };
  exchange_code: string;
}

const StaffLinkEntry: React.FC<StaffLinkEntryProps> = ({ onEnter = load }) => {
  const staff = useSelector((state: { userData?: { isStaff?: boolean } }) =>
    Boolean(state.userData?.isStaff)
  );
  const { pathname, search } = useLocation();
  const path = `${pathname}${search}`;
  // The paths this page has asked about: at most once each per page.
  const asked = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!staff || isClientEnvironment()) return;
    if (!namesResource(path)) return;
    const controller = new AbortController();
    const ask = async (): Promise<void> => {
      if (asked.current.has(path) || knownToOpenNothing(path)) return;
      asked.current.add(path);
      let response: Response;
      try {
        response = await fpFetch(FOR_PATH_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ path }),
          signal: controller.signal,
        });
      } catch {
        return; // aborted with the page, or the network: the page's error stays
      }
      if (response.status === 404 || response.status === 409) {
        rememberOpensNothing(path);
        return;
      }
      if (!response.ok) return;
      const opened = (await response.json()) as OpenedForPath;
      if (holdsTokenFor(opened.grant.id)) {
        // This browser already holds that client's token: join it, without
        // spending the code (which would replace the token other client
        // tabs are using).
        markTabForClient();
        onEnter(path);
        return;
      }
      onEnter(clientEnvironmentEnterUrl(opened.exchange_code, path));
    };
    const onRefused = (): void => {
      void ask();
    };
    window.addEventListener(RESOURCE_REFUSED_EVENT, onRefused);
    return () => {
      window.removeEventListener(RESOURCE_REFUSED_EVENT, onRefused);
      controller.abort();
    };
  }, [staff, path, onEnter]);

  return null;
};

export default StaffLinkEntry;
